import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChartElementEditor from './ChartElementEditor';
import type { ChartSection, ChartElement, ChartWaypoint } from '@/types';

const CHART_SECTION_STYLES = {
  TITLE: 'title',
  H1: 'h1',
  H2: 'h2',
  H3: 'h3',
  H4: 'h4',
  H5: 'h5',
  H6: 'h6',
} as const;

interface ChartSectionEditorProps {
  section: ChartSection;
  elements: ChartElement[];
  onSectionChange: (section: ChartSection) => void;
  onElementsChange: (elements: ChartElement[]) => void;
  onDeleteSection: () => void;
  onAddSection: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isEditable: boolean;
  editMode?: 'full' | 'prompt-only';
  waypoints: ChartWaypoint[];
  isFirst: boolean;
  isLast: boolean;
}

function ChartSectionEditor({
  section,
  elements,
  onSectionChange,
  onElementsChange,
  onDeleteSection,
  onAddSection: _onAddSection,
  onMoveUp,
  onMoveDown,
  isEditable,
  editMode = 'full',
  waypoints,
  isFirst,
  isLast,
}: ChartSectionEditorProps): React.JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(true);

  const handleHeadingChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onSectionChange({ ...section, heading: event.target.value });
  };

  const handleStyleChange = (event: { target: { value: unknown } }): void => {
    onSectionChange({ ...section, style: event.target.value as string });
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onSectionChange({ ...section, number: event.target.value || null });
  };

  const handleElementChange = (elementId: string | undefined, updatedElement: ChartElement): void => {
    const newElements = elements.map((el) =>
      el.entity_id === elementId ? { ...el, ...updatedElement } : el,
    );
    onElementsChange(newElements);
  };

  const handleDeleteElement = (elementId: string | undefined): void => {
    const newElements = elements.filter((el) => el.entity_id !== elementId);
    onElementsChange(newElements);
  };

  const handleAddElement = (): void => {
    const newElement: ChartElement = {
      type: 'narrative',
      description: '',
      prompt: '',
      for_each: false,
      waypoints: [],
      metadata: null,
      order: elements.length,
      section_id: section.entity_id || null,
    };
    onElementsChange([...elements, newElement]);
  };

  const isTitleSection = section.style === 'title';

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{ mb: 2 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}
        >
          {isEditable && editMode === 'full' && (
            <>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  if (onMoveUp) onMoveUp();
                }}
                disabled={isFirst}
                sx={{ opacity: isFirst ? 0.3 : 1 }}
              >
                <ArrowUpwardIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  if (onMoveDown) onMoveDown();
                }}
                disabled={isLast}
                sx={{ opacity: isLast ? 0.3 : 1 }}
              >
                <ArrowDownwardIcon fontSize='small' />
              </IconButton>
            </>
          )}
          <Typography
            variant='subtitle1'
            sx={{ flexGrow: 1, fontWeight: 'bold' }}
          >
            {isTitleSection
              ? 'Title Section'
              : section.heading || 'Untitled Section'}
          </Typography>
          {isEditable && editMode === 'full' && (
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                onDeleteSection();
              }}
              color='error'
            >
              <DeleteOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {!isTitleSection && (
            <TextField
              fullWidth
              label='Section Heading'
              value={section.heading || ''}
              onChange={handleHeadingChange}
              disabled={!isEditable || editMode === 'prompt-only'}
              size='small'
            />
          )}

          <FormControl
            fullWidth
            size='small'
            disabled={!isEditable || editMode === 'prompt-only'}
          >
            <InputLabel>Section Style</InputLabel>
            <Select
              value={section.style || 'h1'}
              onChange={handleStyleChange}
              label='Section Style'
            >
              <MenuItem value={CHART_SECTION_STYLES.TITLE}>Title</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H1}>H1</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H2}>H2</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H3}>H3</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H4}>H4</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H5}>H5</MenuItem>
              <MenuItem value={CHART_SECTION_STYLES.H6}>H6</MenuItem>
            </Select>
          </FormControl>

          {!isTitleSection && (
            <TextField
              fullWidth
              label='Section Number (optional)'
              value={section.number || ''}
              onChange={handleNumberChange}
              disabled={!isEditable || editMode === 'prompt-only'}
              size='small'
              placeholder='e.g., 1, 2, 3'
            />
          )}

          {!isTitleSection && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold' }}>
                  Elements ({elements.length})
                </Typography>
                {isEditable && editMode === 'full' && (
                  <Button
                    size='small'
                    startIcon={<AddOutlinedIcon />}
                    onClick={handleAddElement}
                    variant='outlined'
                  >
                    Add Element
                  </Button>
                )}
              </Box>

              {elements.length === 0 ? (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ fontStyle: 'italic' }}
                >
                  No elements in this section. Click "Add Element" to add one.
                </Typography>
              ) : (
                elements
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((element, elementIndex) => (
                    <ChartElementEditor
                      key={element.entity_id || `element-${elementIndex}`}
                      element={element}
                      onElementChange={(updated) =>
                        handleElementChange(element.entity_id, updated)
                      }
                      onDelete={() => handleDeleteElement(element.entity_id)}
                      onMoveUp={() => {
                        const sortedElements = [...elements].sort(
                          (a, b) => (a.order || 0) - (b.order || 0),
                        );
                        if (elementIndex > 0) {
                          // Swap the elements
                          [
                            sortedElements[elementIndex],
                            sortedElements[elementIndex - 1],
                          ] = [
                            sortedElements[elementIndex - 1],
                            sortedElements[elementIndex],
                          ];
                          // Recalculate all order values to be sequential
                          sortedElements.forEach((el, idx) => {
                            el.order = idx;
                          });
                          onElementsChange(sortedElements);
                        }
                      }}
                      onMoveDown={() => {
                        const sortedElements = [...elements].sort(
                          (a, b) => (a.order || 0) - (b.order || 0),
                        );
                        if (elementIndex < sortedElements.length - 1) {
                          // Swap the elements
                          [
                            sortedElements[elementIndex],
                            sortedElements[elementIndex + 1],
                          ] = [
                            sortedElements[elementIndex + 1],
                            sortedElements[elementIndex],
                          ];
                          // Recalculate all order values to be sequential
                          sortedElements.forEach((el, idx) => {
                            el.order = idx;
                          });
                          onElementsChange(sortedElements);
                        }
                      }}
                      isFirst={elementIndex === 0}
                      isLast={elementIndex === elements.length - 1}
                      isEditable={isEditable}
                      editMode={editMode}
                      waypoints={waypoints}
                    />
                  ))
              )}
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export default ChartSectionEditor;

